import type { SocialPlatform } from "@brandex/api-client";
import type { MessageKey } from "@brandex/i18n";
import { Badge, cn } from "@brandex/ui";
import { CheckCircle2, Circle, CircleDot, ExternalLink, LifeBuoy } from "lucide-react";

import { useT } from "../../context";
import { GUIDES, PROBLEMS, type Problem, type SocialKey, type StepState } from "./guides";

const ICON: Record<StepState, typeof Circle> = { done: CheckCircle2, todo: CircleDot, unknown: Circle };

/**
 * How to connect a platform, as a checklist that ticks itself off from what
 * the panel knows (owner, 2026-11-04). Staff see who does each step; the
 * doctor sees the same steps addressed to them. A sync problem opens the
 * matching "Common problems" entry.
 */
export function ConnectGuide({
  platform,
  panel,
  staff,
  open,
}: {
  platform: SocialKey;
  panel: SocialPlatform;
  staff: boolean;
  /** Folded into a link once the platform works. */
  open: boolean;
}) {
  const t = useT();
  const guide = GUIDES[platform];
  const name = t(`social.platform.${platform}` as MessageKey);
  const problem = panel.sync?.problem?.code;
  const steps = guide.steps.map((s, i) => {
    const state: StepState = s.state?.(panel) ?? "unknown";
    const Icon = ICON[state];
    const base = `social.guide.${platform}.${s.id}`;
    return (
      <li key={s.id} className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
        <Icon
          aria-hidden
          className={cn("mt-0.5 h-5 w-5", state === "done" ? "text-mint-700 dark:text-mint-400" : state === "todo" ? "text-primary" : "text-muted-foreground")}
        />
        <div className="grid gap-1">
          <p className="flex flex-wrap items-center gap-2 font-medium">
            <span>
              {i + 1}. {t(`${base}.title` as MessageKey, { platform: name })}
            </span>
            <span className="sr-only">{t(`social.guide.state.${state}` as MessageKey)}</span>
            {staff && s.who === "doctor" ? <Badge tone="warning">{t("social.guide.doctorStep")}</Badge> : null}
          </p>
          <p className="text-sm text-muted-foreground">{t(`${base}.body` as MessageKey, { platform: name })}</p>
          {s.help !== undefined ? (
            <a href={s.help} target="_blank" rel="noopener noreferrer" className="inline-flex w-fit items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline">
              {t("social.guide.help", { platform: name })}
              <ExternalLink aria-hidden className="h-3.5 w-3.5" />
            </a>
          ) : null}
        </div>
      </li>
    );
  });
  const body = (
    <div className="grid gap-5">
      {staff ? <p className="text-sm text-muted-foreground">{t("social.guide.staffNote")}</p> : null}
      <ol className="grid gap-4">{steps}</ol>
      <Problems active={PROBLEMS.find((p) => p === problem)} platform={name} />
    </div>
  );
  return (
    <section aria-labelledby={`guide-${platform}`} className="rounded-[var(--radius-lg)] border border-border bg-card p-5">
      {open ? (
        <>
          <h2 id={`guide-${platform}`} className="mb-4 flex items-center gap-2 font-display text-lg font-semibold">
            <LifeBuoy aria-hidden className="h-5 w-5 text-primary" />
            {t("social.guide.title", { platform: name })}
          </h2>
          {body}
        </>
      ) : (
        <details>
          <summary id={`guide-${platform}`} className="flex cursor-pointer items-center gap-2 font-medium">
            <LifeBuoy aria-hidden className="h-4 w-4 text-primary" />
            {t("social.guide.howItWorks", { platform: name })}
          </summary>
          <div className="mt-4">{body}</div>
        </details>
      )}
    </section>
  );
}

/** Common problems; the one behind the current sync error is open. */
function Problems({ active, platform }: { active: Problem | undefined; platform: string }) {
  const t = useT();
  return (
    <div className="grid gap-2">
      <h3 className="text-sm font-semibold">{t("social.problem.title")}</h3>
      {PROBLEMS.map((p) => (
        <details key={p} open={p === active} className={cn("rounded-md border px-3 py-2 text-sm", p === active ? "border-coral-500/50 bg-coral-500/5" : "border-border")}>
          <summary className="cursor-pointer font-medium">{t(`social.problem.${p}.title` as MessageKey, { platform })}</summary>
          <p className="mt-2 text-muted-foreground">{t(`social.problem.${p}.body` as MessageKey, { platform })}</p>
        </details>
      ))}
    </div>
  );
}
